import { useState } from 'react'

import {
  shareBrief as shareBriefApi,
  toggleBriefLink as toggleBriefLinkApi,
} from '@disco/api/briefs'
import { updateBriefInPanes } from '@disco/state-management/actions/ExplorerActions'
import {
  briefsApi,
  useGetAssignedBriefRecipientsQuery,
} from '@disco/state-management/api/briefs'
import { loadBrief } from '@disco/state-management/reducers/entities/briefs'

import { BriefRecipientContact } from 'types/brief'

import { useDispatch } from 'hooks/use-redux'

export type AssignedBriefUrl = BriefRecipientContact

export type AssignContactInput = {
  email?: string
  name?: string
  /** Existing DISCO contact id. When present we send the `{ id, type: 'user' }`
   * variant so the backend reuses the contact and any URL already assigned. */
  contactId?: number
}

export type AssignUrlResult = {
  row: AssignedBriefUrl
  duplicate: boolean
}

export const useAssignBriefUrl = (briefId: number) => {
  const dispatch = useDispatch()
  // Refetch on remount so returning from the manage-access modal (which mutates
  // link_enabled outside this RTKQ cache) picks up the latest server state.
  const { data, isLoading, error } = useGetAssignedBriefRecipientsQuery(
    briefId,
    { refetchOnMountOrArgChange: true },
  )
  const urls = data ?? []

  const [isAssigning, setIsAssigning] = useState(false)
  const [isTogglingContactId, setIsTogglingContactId] = useState<number | null>(
    null,
  )

  const assignUrl = async (
    contact: AssignContactInput,
  ): Promise<AssignUrlResult> => {
    setIsAssigning(true)

    try {
      const emailKey = contact.email?.toLowerCase() ?? ''
      const nameKey = contact.name?.toLowerCase() ?? ''
      const matchesContact = (u: BriefRecipientContact) => {
        if (contact.contactId != null) return u.contact_id === contact.contactId
        if (emailKey) return u.email.toLowerCase() === emailKey
        return !!nameKey && u.name.toLowerCase() === nameKey
      }
      const existingBefore = urls.find(matchesContact)

      // Backend requires a discriminator on both variants — omitting `type`
      // triggers "Invalid recipient type: None".
      const recipientPayload =
        contact.contactId != null
          ? {
              id: contact.contactId,
              type: 'user' as const,
              saved_recipients_id: null,
            }
          : {
              type: 'email' as const,
              email: contact.email ?? '',
              ...(contact.name ? { name: contact.name } : {}),
            }

      const response = await shareBriefApi(briefId, {
        recipients: [recipientPayload],
        skip_email: true,
      })

      // Refresh sent_status / Draft badge without a hard refresh. Best-effort.
      try {
        const refreshedBrief = await dispatch(loadBrief(briefId)).unwrap()
        dispatch(updateBriefInPanes(refreshedBrief))
      } catch {
        // Assignment already succeeded — refresh failure is non-fatal.
      }

      const refreshed = await dispatch(
        briefsApi.endpoints.getAssignedBriefRecipients.initiate(briefId, {
          forceRefetch: true,
        }),
      ).unwrap()

      const row =
        response.recipients?.find(matchesContact) ??
        response.recipients?.[0] ??
        refreshed.find(matchesContact) ??
        existingBefore

      if (!row) {
        throw new Error('Assigned URL not found in refreshed list')
      }
      return { row, duplicate: !!existingBefore }
    } finally {
      setIsAssigning(false)
    }
  }

  const toggleDisabled = async (
    row: AssignedBriefUrl,
  ): Promise<AssignedBriefUrl | null> => {
    setIsTogglingContactId(row.contact_id)

    try {
      const nextEnabled = !row.link_enabled
      const response = await toggleBriefLinkApi(briefId, {
        recipients: [
          {
            contact_id: row.contact_id,
            saved_recipients_id: row.saved_recipients_id,
            enabled: nextEnabled,
          },
        ],
      })

      const serverRow = response.recipients?.find(
        r => r.contact_id === row.contact_id,
      )

      let updated: AssignedBriefUrl | null = null

      dispatch(
        briefsApi.util.updateQueryData(
          'getAssignedBriefRecipients',
          briefId,
          draft => {
            const idx = draft.findIndex(r => r.contact_id === row.contact_id)
            if (idx === -1) return
            const merged = serverRow
              ? { ...draft[idx], ...serverRow }
              : { ...draft[idx], link_enabled: nextEnabled }
            // eslint-disable-next-line no-param-reassign
            draft[idx] = merged
            updated = merged
          },
        ),
      )
      return updated
    } finally {
      setIsTogglingContactId(null)
    }
  }

  const errorMessage = error ? 'Failed to load assigned URLs' : null

  return {
    urls,
    isLoading,
    isAssigning,
    isTogglingContactId,
    error: errorMessage,
    assignUrl,
    toggleDisabled,
  }
}
